/**
 * PTL Public Stats — "$X paid out to listeners this month" + Founding
 * Listener slots remaining. Pure social-proof + scarcity bar for the
 * landing / Paid-To-Listen Info pages.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PayoutsCounter({ className = '' }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/ptl/payouts-stats-public`)
      .then((r) => alive && setStats(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!stats) return null;
  // iter_407 — compute the displayed month from the user's LOCAL clock,
  // not from the API's `month` field. The API string is built off the
  // server's UTC clock at the moment of the request, but the value can
  // get stuck on a stale CDN edge / service-worker cache after the month
  // rolls over — leading to "April" still showing in May. Local Date
  // is always correct relative to what the user sees on the rest of the
  // OS, and that's the perception that matters here.
  const monthLabel = new Date().toLocaleString('en-US', { month: 'long' });
  return (
    <div
      className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#00ff88]/10 border border-[#00ff88]/30 ${className}`}
      data-testid="ptl-payouts-counter"
    >
      <DollarSign size={16} className="text-[#00ff88]" />
      <span className="font-mono text-xs sm:text-sm">
        <span className="text-[#00ff88] font-bold tabular-nums">
          ${stats.monthly_total_usd.toFixed(2)}
        </span>{' '}
        <span className="text-white/70">
          paid out to listeners this {monthLabel}
        </span>
      </span>
    </div>
  );
}

export function FoundingListenerBar({ className = '' }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/ptl/founding-listener-status`)
      .then((r) => alive && setData(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!data || data.remaining <= 0) return null;
  const pct = data.percent_claimed;
  return (
    <div
      className={`rounded-xl border border-[#FFDA33]/40 bg-gradient-to-r from-[#FFDA33]/10 via-[#FFDA33]/5 to-transparent p-4 ${className}`}
      data-testid="ptl-founding-bar"
    >
      <div className="flex items-center gap-2 mb-2">
        <Crown size={16} className="text-[#FFDA33]" />
        <span className="font-['Chakra_Petch'] uppercase tracking-wider text-sm text-white font-bold">
          Founding Listener
        </span>
        <span className="ml-auto text-xs font-mono text-[#FFDA33] font-bold tabular-nums">
          {data.remaining.toLocaleString()} of {data.limit.toLocaleString()} spots left
        </span>
      </div>
      <div className="h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#FFDA33] to-[#FFE97A] transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-[#aaa] font-mono">
        First {data.limit.toLocaleString()} listeners get a permanent profile flair +
        a <span className="text-[#FFDA33] font-bold">1.5× earnings boost for life</span>.
      </p>
    </div>
  );
}
